import './App.scss'
import Navigator from "./navigation/navigator.tsx";
import AlertComponent from "@/components/ui/alertComponent.tsx";
import {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {ENV} from "./constants";
import mixpanel from "mixpanel-browser";
import CommonService from "@/services/common.service.ts";
import {userLogout} from "@/store/actions/auth.action.ts";
import {getSubscriptions, getTopupPlans, getUsersTokensUtilization} from "@/store/actions/meta.action.ts";
import {IRootReducerState} from "@/store/reducers";
import {useNavigate} from "react-router-dom";
import {RESTRICTED_ROUTE} from "@/constants/RoutesConfig.tsx";
import * as Sentry from "@sentry/react";
import AppVersionComponent from "@/components/common-components/app-version/AppVersionComponent.tsx";
import UpgradeOrTopupDialog from "@/components/common-components/upgrade-or-topup-dialog/UpgradeOrTopupDialog.tsx";


const mixpanelToken = ENV.MIXPANEL_TOKEN ?? "f436725e3c3b42a518e70ba600eb1db0";

// Only initialize Mixpanel in prod, test, or uat (not dev or localhost)
if(ENV.ENV_MODE === 'prod'){
    if(window.location.hostname !== 'localhost') {
        mixpanel.init(mixpanelToken,{
            debug: true,
            ignore_dnt: true,
            track_pageview: "full-url",
            record_sessions_percent: 1,
            batch_requests: true,
        });
    }
}

//if url is not local host
if(window.location.hostname!== 'localhost') {
    Sentry.init({
        dsn: "https://c4be5e781a13f772e91c543521339a66@o4509355263262720.ingest.us.sentry.io/4509355264311296",
        // Setting this option to true will send default PII data to Sentry.
        // For example, automatic IP address collection on events
        sendDefaultPii: true
    });
}


// Helper function to conditionally load tracking scripts
const loadTrackingScripts = () => {
    // Only load in prod, test, or uat (not dev or localhost)
    const shouldLoadTracking = 
        (ENV.ENV_MODE === 'prod') &&
        window.location.hostname !== 'localhost';

    if (!shouldLoadTracking) {
        return;
    }

    // Load Google Tag Manager
    if (!(window as any).dataLayer) {
        (window as any).dataLayer = [];
    }
    
    // GTM script
    const gtmScript = document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-5BMWT747';
    gtmScript.onload = () => {
        (window as any).dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    };
    document.head.appendChild(gtmScript);

    // Google Analytics
    const gaScript = document.createElement('script');
    gaScript.async = true;
    gaScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-KTLDHBS4C3';
    document.head.appendChild(gaScript);

    // gtag config
    const gtagScript = document.createElement('script');
    gtagScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-KTLDHBS4C3');
    `;
    document.head.appendChild(gtagScript);

    // Hotjar
    const hotjarScript = document.createElement('script');
    hotjarScript.innerHTML = `
        (function (h, o, t, j, a, r) {
            h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments) };
            h._hjSettings = { hjid: 5241583, hjsv: 6 };
            var headElement = o.getElementsByTagName('head')[0];
            var scriptElement = o.createElement('script');
            scriptElement.async = true;
            scriptElement.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
            headElement.appendChild(scriptElement);
        })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
    `;
    document.head.appendChild(hotjarScript);

    // Rybbit analytics
    const rybbitScript = document.createElement('script');
    rybbitScript.src = 'https://app.rybbit.io/api/script.js';
    rybbitScript.setAttribute('data-site-id', '6db0b4906ed8');
    rybbitScript.defer = true;
    document.head.appendChild(rybbitScript);
};

function App() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Load tracking scripts conditionally on mount
    useEffect(() => {
        loadTrackingScripts();
    }, []);

    const token = useSelector((state:IRootReducerState) => state.auth.token)
    
    // State for UpgradeOrTopupDialog
    const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
    const [upgradeMessage, setUpgradeMessage] = useState<string | undefined>();

    useEffect(() => {
        dispatch(getSubscriptions())
        dispatch(getTopupPlans())   
        if (token){
            dispatch(getUsersTokensUtilization())
        }
    }, [dispatch,token])

    const logoutSubscriptionRef = useRef(true);

    useEffect(() => {
        CommonService._communications.logoutSubject.subscribe(() => {
            if (!logoutSubscriptionRef.current) return null;
            dispatch(userLogout());
        });
        const  restrictedAccess= CommonService._communications.RestrictedAccessSubject.subscribe((data:boolean) => {
            if (data){
                navigate(RESTRICTED_ROUTE)
            }
        })
        
        // Subscribe to showUpgradeOrTopupDialogSubject
        const upgradeDialogSubscription = CommonService._communications.showUpgradeOrTopupDialogSubject.subscribe((data: { message?: string }) => {
            setUpgradeMessage(data.message);
            setIsUpgradeDialogOpen(true);
        });

        return () => {
            logoutSubscriptionRef.current = false;
            restrictedAccess.unsubscribe();
            upgradeDialogSubscription.unsubscribe();
        }
    }, [dispatch,navigate]);

    return (
        <>
            <AlertComponent/>
                <Navigator/>
            <AppVersionComponent/>
            <UpgradeOrTopupDialog 
                isOpen={isUpgradeDialogOpen}
                onClose={() => setIsUpgradeDialogOpen(false)}
                message={upgradeMessage}
            />
        </>
    )
}

export default App
